import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { UserPlus, UserCheck, Check, X, MessageCircle } from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import { useChatStore } from '@/store/chatStore';
import { useThemeContext } from './ThemeContext';
import { hasAnySocialLink } from '@/lib/socialLinks';

function Avatar({ name, avatar }: { name: string; avatar: string | null }) {
  const [failed, setFailed] = useState(false);
  if (avatar && !failed) {
    return (
      <img
        src={avatar}
        alt=""
        referrerPolicy="no-referrer"
        className="w-10 h-10 rounded-full object-cover shrink-0"
        onError={() => setFailed(true)}
      />
    );
  }
  return (
    <span className="w-10 h-10 rounded-full shrink-0 flex items-center justify-center text-sm font-semibold bg-gradient-to-br from-[#ff3366] to-pink-500 text-white">
      {name.charAt(0).toUpperCase()}
    </span>
  );
}

/**
 * Global toasts for friend-request received/accepted events, shown anywhere in the app.
 */
export function FriendRequestNotifier() {
  const navigate = useNavigate();
  const { isLight } = useThemeContext();
  const sessionReady = useAuthStore((s) => s.sessionReady);
  const accountType = useAuthStore((s) => s.user?.accountType);
  const alert = useChatStore((s) => s.friendRequestAlert);
  const alertSeq = useChatStore((s) => s.friendRequestAlertSeq);
  const acceptRequest = useChatStore((s) => s.acceptRequest);
  const declineRequest = useChatStore((s) => s.declineRequest);
  const createConversationWithFriend = useChatStore((s) => s.createConversationWithFriend);

  useEffect(() => {
    if (!sessionReady || accountType !== 'member' || !alert || alertSeq === 0) return;

    const cardCls = isLight
      ? 'bg-white/95 border-zinc-200/80 text-zinc-900'
      : 'bg-[#12121c]/95 border-white/10 text-white';
    const mutedCls = isLight ? 'text-zinc-500' : 'text-zinc-400';
    const secondaryBtnCls = isLight
      ? 'bg-zinc-100 border border-zinc-200 text-zinc-700 hover:bg-zinc-200'
      : 'bg-white/[0.06] border border-white/10 text-gray-200 hover:bg-white/[0.1]';

    if (alert.type === 'received') {
      toast.custom(
        (id) => (
          <div className={`flex items-start gap-3 rounded-xl border shadow-2xl px-4 py-3.5 w-[min(360px,90vw)] backdrop-blur-xl ${cardCls}`}>
            <Avatar name={alert.userName} avatar={alert.userAvatar} />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold leading-snug flex items-center gap-1.5">
                <UserPlus size={14} className="text-[#ff3366] shrink-0" />
                Friend request
              </p>
              <p className={`text-xs mt-0.5 ${mutedCls}`}>
                <span className="font-medium">{alert.userName}</span> wants to be friends.
              </p>
              <div className="flex items-center gap-2 mt-2.5">
                <button
                  type="button"
                  onClick={async () => {
                    toast.dismiss(id);
                    await acceptRequest(alert.requestId);
                  }}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-gradient-to-r from-[#ff3366] to-pink-500 text-white hover:opacity-90 transition-opacity"
                >
                  <Check size={13} />
                  Accept
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    toast.dismiss(id);
                    await declineRequest(alert.requestId);
                  }}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${secondaryBtnCls}`}
                >
                  <X size={13} />
                  Decline
                </button>
              </div>
            </div>
          </div>
        ),
        { duration: 10000, unstyled: true }
      );
      return;
    }

    // alert.type === 'accepted'
    toast.custom(
      (id) => (
        <div className={`flex items-start gap-3 rounded-xl border shadow-2xl px-4 py-3.5 w-[min(360px,90vw)] backdrop-blur-xl ${cardCls}`}>
          <Avatar name={alert.userName} avatar={alert.userAvatar} />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold leading-snug flex items-center gap-1.5">
              <UserCheck size={14} className="text-[#ff3366] shrink-0" />
              New friend
            </p>
            <p className={`text-xs mt-0.5 ${mutedCls}`}>
              <span className="font-medium">{alert.userName}</span> accepted your friend request.
            </p>
            <div className="flex items-center gap-2 mt-2.5">
              <button
                type="button"
                onClick={async () => {
                  toast.dismiss(id);
                  const me = useAuthStore.getState().user;
                  if (!me?.isSelfiVerified || !hasAnySocialLink(me?.social)) {
                    toast.error('Verify your photo and add a social link to start messaging.');
                    navigate('/member/?section=profile', { preventScrollReset: true });
                    return;
                  }
                  const conversationId = await createConversationWithFriend(alert.userId);
                  navigate(
                    conversationId ? `/member/chat?view=inbox&conversation=${conversationId}` : '/member/chat',
                    { preventScrollReset: true }
                  );
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-gradient-to-r from-[#ff3366] to-pink-500 text-white hover:opacity-90 transition-opacity"
              >
                <MessageCircle size={13} />
                Message
              </button>
              <button
                type="button"
                onClick={() => {
                  toast.dismiss(id);
                  navigate('/member/friends', { preventScrollReset: true });
                }}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${secondaryBtnCls}`}
              >
                View Friends
              </button>
            </div>
          </div>
        </div>
      ),
      { duration: 8000, unstyled: true }
    );
  }, [sessionReady, accountType, alert, alertSeq, isLight, navigate, acceptRequest, declineRequest, createConversationWithFriend]);

  return null;
}
